import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import './HoroscopePage.css'
import PageLayout from '../components/layout/PageLayout'
import Reveal from '../components/common/Reveal'
import { useToast } from '../context/ToastContext'
import { getPlanetTransits, buildTransitReading } from '@astro/vedastro'
import { appUrl } from '@astro/config'

/* ─────────── Static data ─────────── */
const SIGNS = [
  { name: 'Aries', glyph: '♈', dates: 'Mar 21 – Apr 19', element: 'Fire', ruler: 'Mars', desc: 'Bold, energetic and a natural pioneer who charges ahead with courage.' },
  { name: 'Taurus', glyph: '♉', dates: 'Apr 20 – May 20', element: 'Earth', ruler: 'Venus', desc: 'Grounded, loyal and patient, with a deep love of comfort and beauty.' },
  { name: 'Gemini', glyph: '♊', dates: 'May 21 – Jun 20', element: 'Air', ruler: 'Mercury', desc: 'Curious, witty and adaptable, forever exchanging ideas and stories.' },
  { name: 'Cancer', glyph: '♋', dates: 'Jun 21 – Jul 22', element: 'Water', ruler: 'Moon', desc: 'Nurturing, intuitive and protective of the ones they hold dear.' },
  { name: 'Leo', glyph: '♌', dates: 'Jul 23 – Aug 22', element: 'Fire', ruler: 'Sun', desc: 'Warm, generous and magnetic, born to lead and to shine.' },
  { name: 'Virgo', glyph: '♍', dates: 'Aug 23 – Sep 22', element: 'Earth', ruler: 'Mercury', desc: 'Practical, precise and devoted, with a gift for quiet service.' },
  { name: 'Libra', glyph: '♎', dates: 'Sep 23 – Oct 22', element: 'Air', ruler: 'Venus', desc: 'Diplomatic, charming and fair, always seeking balance and harmony.' },
  { name: 'Scorpio', glyph: '♏', dates: 'Oct 23 – Nov 21', element: 'Water', ruler: 'Mars / Pluto', desc: 'Intense, passionate and perceptive, with formidable inner depth.' },
  { name: 'Sagittarius', glyph: '♐', dates: 'Nov 22 – Dec 21', element: 'Fire', ruler: 'Jupiter', desc: 'Optimistic, adventurous and free, forever chasing meaning and horizons.' },
  { name: 'Capricorn', glyph: '♑', dates: 'Dec 22 – Jan 19', element: 'Earth', ruler: 'Saturn', desc: 'Disciplined, ambitious and wise, building lasting success with patience.' },
  { name: 'Aquarius', glyph: '♒', dates: 'Jan 20 – Feb 18', element: 'Air', ruler: 'Saturn / Uranus', desc: 'Visionary, independent and humane, devoted to ideas and progress.' },
  { name: 'Pisces', glyph: '♓', dates: 'Feb 19 – Mar 20', element: 'Water', ruler: 'Jupiter / Neptune', desc: 'Compassionate, dreamy and artistic, deeply attuned to the unseen.' },
]

const PERIODS = [
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'today', label: 'Today' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
]

const PERIOD_LABELS = { yesterday: 'Yesterday', today: 'Today', tomorrow: 'Tomorrow', weekly: 'This Week', monthly: 'This Month' }

// The sky date each period reads from (mid-week / mid-month ahead so periods differ).
function periodDate(period) {
  const offset = { yesterday: -1, today: 0, tomorrow: 1, weekly: 3, monthly: 15 }[period] ?? 0
  const d = new Date()
  d.setDate(d.getDate() + offset)
  return d
}

function fmtReadingDate(date) {
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })
}

/* ─────────── Component ─────────── */
export default function HoroscopePage() {
  const { showToast } = useToast()
  const [currentSign, setCurrentSign] = useState('Aries')
  const [currentPeriod, setCurrentPeriod] = useState('today')
  const [view, setView] = useState('load') // 'load' | 'error' | 'content'
  const [result, setResult] = useState({ text: '', date: '', luckyNum: '', luckyColor: '', luckyMood: '' })
  const reqIdRef = useRef(0)
  const cacheRef = useRef({}) // dateStr → planet transits (avoids refetching per sign)

  const activeSign = SIGNS.find((s) => s.name === currentSign)

  const loadHoroscope = useCallback(async () => {
    const myReq = ++reqIdRef.current
    setView('load')
    try {
      const date = periodDate(currentPeriod)
      const dateStr = date.toISOString().slice(0, 10)
      // Transits are absolute per date — fetch once, reuse across all 12 signs.
      let transits = cacheRef.current[dateStr]
      if (!transits) {
        transits = await getPlanetTransits(dateStr)
        if (myReq !== reqIdRef.current) return
        cacheRef.current[dateStr] = transits
      }
      const { text, luckyNum, luckyColor, luckyMood } = buildTransitReading(currentSign, currentPeriod, transits)
      setResult({ text, date: fmtReadingDate(date), luckyNum, luckyColor, luckyMood })
      setView('content')
    } catch (err) {
      if (myReq !== reqIdRef.current) return
      setView('error')
      console.error('Horoscope transit fetch failed:', err)
    }
  }, [currentSign, currentPeriod])

  useEffect(() => {
    loadHoroscope()
  }, [loadHoroscope])

  const shareHoroscope = () => {
    const label = PERIOD_LABELS[currentPeriod]
    const share = `${currentSign} — ${label} Horoscope\n\n${result.text}\n\nvia Astrologer Raju`
    if (navigator.share) {
      navigator.share({ title: `${currentSign} Horoscope`, text: share }).catch(() => {})
    } else {
      navigator.clipboard?.writeText(share)
      showToast('Horoscope copied to clipboard!')
    }
  }

  const periodLabel = PERIOD_LABELS[currentPeriod]

  return (
    <PageLayout className="horoscope-page text-astroDark antialiased overflow-x-hidden">

      {/* ── HERO / BREADCRUMB BANNER ── */}
      <header className="relative pt-[120px] sm:pt-[130px] lg:pt-[150px] pb-20 lg:pb-28 overflow-hidden bg-[#5A0808] text-white text-center border-b border-[#7A0A0A]">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[70vw] h-[70vw] bg-cosmicRed/40 rounded-full animate-aura blur-3xl" />
          <svg
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] aspect-square opacity-[0.08] text-goldPrimary animate-spin-slow"
            viewBox="0 0 200 200"
            fill="none"
            stroke="currentColor"
          >
            <circle cx="100" cy="100" r="98" strokeWidth="0.5" />
            <circle cx="100" cy="100" r="74" strokeWidth="0.5" strokeDasharray="2 4" />
            <circle cx="100" cy="100" r="50" strokeWidth="0.5" />
            <path d="M100 2v196M2 100h196" strokeWidth="0.3" />
          </svg>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="w-8 h-px bg-goldPrimary/50" />
            <span className="text-[9px] uppercase font-bold tracking-[0.4em] text-goldPrimary/80 font-royal">Cosmic Forecast</span>
            <span className="w-8 h-px bg-goldPrimary/50" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-royal text-[#FBF3E4] leading-tight">
            Your{' '}
            <span className="bg-gradient-to-r from-goldPrimary via-amber-300 to-goldAccent bg-clip-text text-transparent">
              Daily Horoscope
            </span>
          </h1>
          <p className="text-sm sm:text-base text-white/60 font-light mt-5 max-w-xl mx-auto leading-relaxed">
            Choose your zodiac sign and discover what the planets have aligned for you — updated live every day.
          </p>
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 mt-7 px-4 text-[10px] uppercase tracking-[0.2em] font-semibold text-white/50">
            <Link to="/" className="hover:text-goldPrimary transition-colors">Home</Link>
            <span className="text-goldPrimary/50">/</span>
            <span className="text-goldPrimary">Horoscope</span>
          </nav>
        </div>
      </header>

      {/* ── HOROSCOPE APP ── */}
      <section
        className="relative py-16 lg:py-24 overflow-hidden"
        style={{ background: 'linear-gradient(180deg,#FFFFFF 0%,#FDFAF5 50%,#FFFFFF 100%)' }}
      >
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
          <div
            className="absolute -top-32 -left-32 w-[55vw] max-w-[640px] h-[55vw] max-h-[640px] rounded-full"
            style={{ background: 'radial-gradient(circle,rgba(245,158,11,.10) 0%,transparent 70%)', filter: 'blur(80px)' }}
          />
          <div
            className="absolute -bottom-32 -right-32 w-[45vw] max-w-[540px] h-[45vw] max-h-[540px] rounded-full"
            style={{ background: 'radial-gradient(circle,rgba(217,119,6,.08) 0%,transparent 70%)', filter: 'blur(90px)' }}
          />
        </div>
        <div className="max-w-6xl mx-auto px-6 lg:px-12 relative z-10">
          {/* Sign selector */}
          <Reveal className="text-center mb-8">
            <span className="font-royal text-[9px] uppercase font-bold tracking-[.45em]" style={{ color: '#B45309' }}>
              Step 1 · Choose Your Sign
            </span>
          </Reveal>
          <Reveal className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4 mb-12">
            {SIGNS.map((s) => (
              <div
                key={s.name}
                className={`sign-tile${s.name === currentSign ? ' active' : ''}`}
                onClick={() => setCurrentSign(s.name)}
              >
                <div className="sign-glyph">{s.glyph}</div>
                <p className="text-[12px] font-semibold text-astroDark mt-2">{s.name}</p>
                <p className="text-[8px] uppercase tracking-wider text-stone-400 mt-0.5">{s.dates}</p>
              </div>
            ))}
          </Reveal>

          {/* Period tabs */}
          <Reveal className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-3 mb-10">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`period-tab${p.key === currentPeriod ? ' active' : ''}`}
                onClick={() => setCurrentPeriod(p.key)}
              >
                {p.label}
              </button>
            ))}
          </Reveal>

          {/* Result card */}
          <Reveal className="max-w-3xl mx-auto">
            <div className="bg-portalLight rounded-[26px] p-7 sm:p-10 shadow-2xl border border-goldPrimary/10">
              {/* Card header */}
              <div className="flex items-center gap-4 pb-6 mb-6 border-b border-stone-200">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0"
                  style={{ background: 'linear-gradient(135deg,rgba(245,158,11,.18),rgba(217,119,6,.06))', border: '1px solid rgba(245,158,11,.25)' }}
                >
                  <span className="text-[34px] text-goldAccent leading-none">{activeSign.glyph}</span>
                </div>
                <div className="min-w-0">
                  <h2 className="text-2xl sm:text-3xl text-astroDark leading-tight">{activeSign.name}</h2>
                  <p className="text-[12px] text-amber-700/80 font-semibold uppercase tracking-wider mt-1">
                    Ruled by {activeSign.ruler.split('/')[0].trim()} · {periodLabel}
                  </p>
                </div>
              </div>

              {/* Loading — skeleton mirroring the reading layout */}
              {view === 'load' && (
                <div className="animate-pulse" aria-hidden="true">
                  {/* Prediction lines */}
                  <div className="space-y-3">
                    <div className="h-3.5 rounded-full bg-stone-200/80 w-full" />
                    <div className="h-3.5 rounded-full bg-stone-200/80 w-[93%]" />
                    <div className="h-3.5 rounded-full bg-stone-200/80 w-[97%]" />
                    <div className="h-3.5 rounded-full bg-stone-200/80 w-[68%]" />
                  </div>
                  {/* Date line */}
                  <div className="h-2.5 rounded-full bg-stone-200/70 w-44 mt-6" />
                  {/* Lucky strip */}
                  <div className="grid grid-cols-3 gap-3 mt-7">
                    {[0, 1, 2].map((i) => (
                      <div
                        key={i}
                        className="rounded-2xl py-4 px-2 flex flex-col items-center gap-2.5"
                        style={{ background: 'rgba(245,158,11,.07)', border: '1px solid rgba(245,158,11,.14)' }}
                      >
                        <div className="h-2 rounded-full bg-stone-200/80 w-14" />
                        <div className="h-5 rounded-full bg-stone-200/80 w-10" />
                      </div>
                    ))}
                  </div>
                  {/* Buttons */}
                  <div className="flex flex-col sm:flex-row items-center gap-3 mt-8">
                    <div className="h-11 rounded-full bg-stone-200/80 w-full sm:w-56" />
                    <div className="h-11 rounded-full bg-stone-200/70 w-full sm:w-32" />
                  </div>
                </div>
              )}

              {/* Error */}
              {view === 'error' && (
                <div className="text-center py-8">
                  <div className="text-3xl mb-3">🔭</div>
                  <p className="text-sm text-stone-600 font-medium">The cosmic connection faltered.</p>
                  <p className="text-[12px] text-stone-400 mt-1.5 font-light">
                    We couldn&apos;t reach the horoscope service right now. Please check your connection and retry.
                  </p>
                  <button
                    type="button"
                    onClick={loadHoroscope}
                    className="mt-5 shine-btn bg-gradient-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[11px] uppercase tracking-[.2em] px-6 py-3 rounded-full"
                  >
                    Retry
                  </button>
                </div>
              )}

              {/* Content */}
              {view === 'content' && (
                <div>
                  <p className="text-[15px] leading-[1.95] text-stone-700 font-light">{result.text}</p>
                  {result.date && (
                    <p className="text-[11px] text-stone-400 uppercase tracking-[.2em] font-semibold mt-6">Reading for {result.date}</p>
                  )}

                  {/* Lucky strip */}
                  <div className="grid grid-cols-3 gap-3 mt-7">
                    <div className="text-center rounded-2xl py-4 px-2" style={{ background: 'rgba(245,158,11,.07)', border: '1px solid rgba(245,158,11,.14)' }}>
                      <p className="text-[9px] uppercase tracking-[.2em] text-stone-500 font-semibold">Lucky No.</p>
                      <p className="num text-2xl font-bold text-goldAccent mt-1">{result.luckyNum}</p>
                    </div>
                    <div className="text-center rounded-2xl py-4 px-2" style={{ background: 'rgba(245,158,11,.07)', border: '1px solid rgba(245,158,11,.14)' }}>
                      <p className="text-[9px] uppercase tracking-[.2em] text-stone-500 font-semibold">Lucky Colour</p>
                      <p className="text-base font-semibold text-astroDark mt-2">{result.luckyColor}</p>
                    </div>
                    <div className="text-center rounded-2xl py-4 px-2" style={{ background: 'rgba(245,158,11,.07)', border: '1px solid rgba(245,158,11,.14)' }}>
                      <p className="text-[9px] uppercase tracking-[.2em] text-stone-500 font-semibold">Mood</p>
                      <p className="text-base font-semibold text-astroDark mt-2">{result.luckyMood}</p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center gap-3 mt-8">
                    <a
                      href={appUrl('jyotish', 'consultations')}
                      className="shine-btn w-full sm:w-auto text-center bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[11px] uppercase tracking-[.2em] px-7 py-3.5 rounded-full shadow-lg"
                    >
                      Get a Personal Reading
                    </a>
                    <button
                      type="button"
                      onClick={shareHoroscope}
                      className="w-full sm:w-auto text-center border border-stone-300 hover:border-goldPrimary text-stone-600 hover:text-goldAccent font-semibold text-[11px] uppercase tracking-[.2em] px-7 py-3.5 rounded-full transition-colors"
                    >
                      Share ✦
                    </button>
                  </div>
                </div>
              )}
            </div>
            <p className="text-center text-[11px] text-stone-400 mt-5 font-light">
              Horoscope data refreshes daily. For predictions specific to your birth chart, book a personal consultation.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── ABOUT THE SIGNS ── */}
      <section className="relative py-16 lg:py-24" style={{ background: '#FDFAF5' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <span className="font-royal text-[9px] uppercase font-bold tracking-[.45em] text-amber-700/70">The Twelve Rashis</span>
            <h2 className="text-3xl lg:text-[40px] text-astroDark mt-3">Understanding Your Zodiac</h2>
            <p className="text-sm text-stone-500 font-light mt-4 leading-relaxed">
              Each sign carries a unique elemental energy and ruling planet that shapes your nature and your path.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SIGNS.map((s) => (
              <Reveal key={s.name} className="val-card p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-3xl text-goldAccent">{s.glyph}</span>
                  <div>
                    <h3 className="text-lg text-astroDark leading-none">{s.name}</h3>
                    <p className="text-[10px] uppercase tracking-wider text-amber-700/70 font-semibold mt-1">{s.dates}</p>
                  </div>
                </div>
                <p className="text-[13px] text-stone-500 font-light leading-relaxed">{s.desc}</p>
                <div className="flex gap-2 mt-4">
                  <span className="text-[9px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700">{s.element}</span>
                  <span className="text-[9px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700">Ruler · {s.ruler}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BAND ── */}
      <section
        className="relative py-16 lg:py-20 overflow-hidden"
        style={{ background: 'radial-gradient(ellipse 120% 100% at 50% 0%,#C2710C 0%,#92400E 30%,#4A1A05 65%,#1A0803 100%)' }}
      >
        <div className="absolute inset-0 pointer-events-none opacity-10" aria-hidden="true">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[200px] font-royal text-white select-none">ॐ</div>
        </div>
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-3xl lg:text-[42px] text-white leading-tight">Want a Forecast Built on Your Birth Chart?</h2>
          <p className="text-sm text-white/70 font-light mt-4 max-w-xl mx-auto">
            General horoscopes are just the beginning. Book a personal Kundli reading with Maharaj Ji for guidance made for you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
            <a
              href={appUrl('jyotish', 'consultations')}
              className="shine-btn bg-white text-astroDark font-bold text-[11px] uppercase tracking-[.22em] px-8 py-4 rounded-full shadow-xl transition-transform hover:-translate-y-0.5"
            >
              Book Consultation
            </a>
            <Link
              to="/contact"
              className="border border-white/40 hover:border-white text-white font-semibold text-[11px] uppercase tracking-[.22em] px-8 py-4 rounded-full transition-all hover:bg-white/10"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

    </PageLayout>
  )
}
